import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import UpdateStudentPerformance from "./UpdatePerformanceTable";
import MultiSelectDropdown from "./formComponents/MultiSelectDropdown";
import { fetchBatchOptions } from "../pages/services/filterOptionsService";
import "../styles/AddForm.css";
import "../styles/AddStudent.css";
import "../styles/UpdateStudent.css";

const API_URL = process.env.REACT_APP_API_URL;

const DAY_ABBR = { Monday: "M", Tuesday: "T", Wednesday: "W", Thursday: "Th", Friday: "F", Saturday: "Sa", Sunday: "Su" };
const formatBatchLabel = (b) => {
    const days = (b.batchDays || []).map(d => DAY_ABBR[d] || d).join("");
    return `${b.batchTitle} | ${days} - ${b.batchTime}`;
};

const HEARD_FROM  = ["Facebook", "Instagram", "Linkedin", "College webinar", "Website", "Friend or relative", "Other"];
const STATUSES    = ["Enquiry", "Registered", "Enrolled", "Completed"];
const EDU_LEVELS  = ["Schooling", "Graduation", "Post Graduation", "Diploma"];

const CURRENT_YEAR = new Date().getFullYear();
const EDU_YEARS    = Array.from({ length: CURRENT_YEAR - 2009 }, (_, i) => CURRENT_YEAR - i);
const FUTURE_YEARS = Array.from({ length: 10 }, (_, i) => CURRENT_YEAR + i);

const Field = ({ label, required, error, children }) => (
    <div className="af-field">
        <label className="af-label">{label}{required && <span className="af-required">*</span>}</label>
        {children}
        {error && <span className="af-error">{error}</span>}
    </div>
);

const BLANK_FORM = {
    studentName: "", contactNumber: "", alternateContactNumber: "",
    DOB: "",
    permanentAddress: "", permanentAddressCity: "", permanentAddressState: "",
    alternateAddress: "", alternateAddressCity: "", alternateAddressState: "",
    emailId: [],
    fatherName: "", fatherOccupation: "", fatherContactNumber: "",
    motherName: "", motherOccupation: "", motherContactNumber: "",
    educationLevel: "",
    schoolCollege: "", schoolCollegePlace: "",
    score: "", branch: "",
    completionYear: "", currentlyPursuing: false, completedIn: "",
    enrollingForCourse: "", heardFrom: "", skillsKnownAlready: "",
    enrolledForCourse: "", totalFee: "", feePaid: "", batchIds: [], status: "", isActive: true, trackingEnabled: true,
};

const UpdateStudent = () => {
    const { id: studentId } = useParams();
    const navigate = useNavigate();

    const [formData, setFormData] = useState(BLANK_FORM);
    const [emailInput, setEmailInput] = useState("");
    const [sameAsPermanent, setSameAsPermanent] = useState(false);
    const [batches, setBatches] = useState([]);
    const [courses, setCourses] = useState([]);
    const [errors, setErrors] = useState({});
    const [studentPerformances, setStudentPerformances] = useState([]);
    const [successMessage, setSuccessMessage] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const [showUpdatePerformancesDialog, setShowUpdatePerformancesDialog] = useState(false);
    const [showDeletePopup, setShowDeletePopup] = useState(false);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [studentRes, batchesRes, coursesRes] = await Promise.all([
                    fetch(`${API_URL}/api/student/${studentId}`),
                    fetchBatchOptions(),
                    fetch(`${API_URL}/api/getAllCourse`),
                ]);

                const studentData = await studentRes.json();
                // batchesRes is already the option array from the shared source.
                const coursesData = await coursesRes.json();

                if (!studentRes.ok || !studentData?.data) {
                    navigate("/not-found", { replace: true });
                    return;
                }

                const s = studentData.data;
                setBatches((batchesRes || []).map(o => ({ _id: o.value, batchTitle: o.label })));
                setCourses(coursesData?.data || []);

                const altSameAsPermanent =
                    s.alternateAddress === s.permanentAddress &&
                    s.alternateAddressCity === s.permanentAddressCity &&
                    s.alternateAddressState === s.permanentAddressState &&
                    !!s.permanentAddress;
                setSameAsPermanent(altSameAsPermanent);

                setFormData({
                    studentName:            s.studentName || "",
                    contactNumber:          s.contactNumber || "",
                    alternateContactNumber: s.alternateContactNumber || "",
                    DOB:                    s.DOB ? new Date(s.DOB).toISOString().split("T")[0] : "",
                    permanentAddress:       s.permanentAddress || "",
                    permanentAddressCity:   s.permanentAddressCity || "",
                    permanentAddressState:  s.permanentAddressState || "",
                    alternateAddress:       s.alternateAddress || "",
                    alternateAddressCity:   s.alternateAddressCity || "",
                    alternateAddressState:  s.alternateAddressState || "",
                    emailId: Array.isArray(s.emailId) ? s.emailId : s.emailId ? [s.emailId] : [],
                    fatherName:             s.fatherName || "",
                    fatherOccupation:       s.fatherOccupation || "",
                    fatherContactNumber:    s.fatherContactNumber || "",
                    motherName:             s.motherName || "",
                    motherOccupation:       s.motherOccupation || "",
                    motherContactNumber:    s.motherContactNumber || "",
                    educationLevel:         s.educationLevel || "",
                    schoolCollege:          s.schoolCollege || "",
                    schoolCollegePlace:     s.schoolCollegePlace || "",
                    score:                  s.score || "",
                    branch:                 s.branch || "",
                    completionYear:         s.completionYear || "",
                    currentlyPursuing:      s.currentlyPursuing || false,
                    completedIn:            s.completedIn || "",
                    enrollingForCourse:     s.enrollingForCourse || "",
                    heardFrom:              s.heardFrom || "",
                    skillsKnownAlready:     s.skillsKnownAlready || "",
                    enrolledForCourse:      s.enrolledForCourse || "",
                    totalFee:               s.totalFee || "",
                    feePaid:                s.feePaid || "",
                    batchIds: Array.isArray(s.batchInfos) && s.batchInfos.length
                        ? s.batchInfos.map(b => b._id)
                        : (s.batchInfo?._id ? [s.batchInfo._id] : []),
                    status:                 s.status || "",
                    isActive:               s.isActive !== false,
                    trackingEnabled:        s.trackingEnabled !== false,
                });
            } catch (err) {
                console.error("Error fetching student:", err);
            }
        };

        const fetchPerformances = async () => {
            try {
                const res = await fetch(`${API_URL}/api/student-performance/${studentId}`);
                const data = await res.json();
                if (res.ok) {
                    const perfs = data.data || data.studentPerformance;
                    setStudentPerformances(Array.isArray(perfs) ? perfs : []);
                }
            } catch (err) {
                console.error("Error fetching performances:", err);
            }
        };

        fetchData();
        fetchPerformances();
    }, [studentId, navigate]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(p => ({ ...p, [name]: value }));
        setErrors(p => ({ ...p, [name]: "" }));
    };

    const batchOptions = batches.map(b => ({ value: b._id, label: formatBatchLabel(b) }));

    // Mapped from batchIds rather than filtered out of batchOptions, so the
    // chips keep the order they were picked in — batchIds[0] is the primary.
    const selectedBatchOptions = formData.batchIds
        .map(id => batchOptions.find(o => String(o.value) === String(id)))
        .filter(Boolean);

    // MultiSelectDropdown deals in {label,value} objects; the form stores ids.
    const handleBatchChange = (selected) => {
        setFormData(p => ({ ...p, batchIds: selected.map(o => o.value) }));
        setErrors(p => ({ ...p, batchIds: "" }));
    };

    const addEmail = () => {
        const email = emailInput.trim();
        if (!email) return;
        if (formData.emailId.includes(email)) {
            setErrors(p => ({ ...p, emailId: "Email already added" }));
            return;
        }
        setFormData(p => ({ ...p, emailId: [...p.emailId, email] }));
        setErrors(p => ({ ...p, emailId: "" }));
        setEmailInput("");
    };

    const removeEmail = (index) => {
        setFormData(p => ({ ...p, emailId: p.emailId.filter((_, i) => i !== index) }));
    };

    const handleEmailKeyDown = (e) => {
        if (e.key === "Enter") { e.preventDefault(); addEmail(); }
    };

    const validate = () => {
        const required = [
            "studentName", "contactNumber", "alternateContactNumber",
            "permanentAddress", "permanentAddressCity", "permanentAddressState",
            ...(!sameAsPermanent ? ["alternateAddress", "alternateAddressCity", "alternateAddressState"] : []),
            "emailId", "fatherName", "fatherOccupation", "fatherContactNumber",
            "motherName", "motherOccupation", "motherContactNumber",
            "educationLevel", "enrollingForCourse", "heardFrom", "batchIds",
        ];
        const labels = {
            studentName: "Student Name", contactNumber: "Contact Number",
            alternateContactNumber: "Alternate Contact", permanentAddress: "Permanent Address",
            permanentAddressCity: "City", permanentAddressState: "State",
            alternateAddress: "Alternate Address", alternateAddressCity: "Alt. City",
            alternateAddressState: "Alt. State", emailId: "Email",
            fatherName: "Father's Name", fatherOccupation: "Father's Occupation",
            fatherContactNumber: "Father's Contact", motherName: "Mother's Name",
            motherOccupation: "Mother's Occupation", motherContactNumber: "Mother's Contact",
            educationLevel: "Last/Current Education",
            enrollingForCourse: "Enrolling For", heardFrom: "Heard From", batchIds: "Batch",
        };
        const errs = {};
        required.forEach(f => {
            if (f === "emailId") {
                if (!formData.emailId || formData.emailId.length === 0)
                    errs.emailId = "At least one email is required";
            } else if (f === "batchIds") {
                if (!formData.batchIds || formData.batchIds.length === 0)
                    errs.batchIds = "At least one batch is required";
            } else if (!String(formData[f] ?? "").trim()) {
                errs[f] = `${labels[f] || f} is required`;
            }
        });

        if (formData.educationLevel && !formData.schoolCollege?.trim()) {
            errs.schoolCollege = "School / College name is required";
        }

        return errs;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const validationErrors = validate();
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            setErrorMessage("Please correct the errors below.");
            return;
        }
        setLoading(true);
        try {
            const response = await fetch(`${API_URL}/api/student/${studentId}`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(formData),
            });
            if (!response.ok) {
                const errData = await response.json();
                throw new Error(errData.message || "Failed to update student");
            }
            navigate("/students", { state: { successMessage: "Student updated successfully!" } });
        } catch (err) {
            setErrorMessage(err.message || "Update failed.");
        } finally {
            setLoading(false);
        }
    };

    const confirmDelete = async () => {
        setLoading(true);
        try {
            for (const perf of studentPerformances) {
                if (!perf?._id) continue;
                await fetch(`${API_URL}/api/student-performance/${perf._id}`, { method: "DELETE" });
            }
            const res = await fetch(`${API_URL}/api/student/${studentId}`, { method: "DELETE" });
            if (!res.ok) throw new Error("Failed to delete student");
            navigate("/students", { state: { successMessage: "Student deleted successfully!" } });
        } catch (err) {
            setErrorMessage(err.message || "Delete failed.");
        } finally {
            setLoading(false);
            setShowDeletePopup(false);
        }
    };

    const inp = (name, placeholder, type = "text") => (
        <input
            type={type} name={name} id={name}
            placeholder={placeholder} value={formData[name]}
            onChange={handleChange}
            className={errors[name] ? "error-input" : ""}
        />
    );

    const sel = (name, options) => (
        <select name={name} value={formData[name]} onChange={handleChange}
            className={errors[name] ? "error-input" : ""}>
            <option value="">Select…</option>
            {options.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
    );

    const textarea = (name, placeholder) => (
        <textarea name={name} placeholder={placeholder} value={formData[name]}
            onChange={handleChange} className={errors[name] ? "error-input" : ""} rows={3} />
    );

    return (
        <div className="af-page">
            {loading && (
                <div className="af-overlay">
                    <div className="af-spinner" />
                    <p className="af-overlay-text">Saving changes…</p>
                </div>
            )}

            {showDeletePopup && (
                <div className="delete-student-popup-overlay">
                    <div className="student-delete-popup">
                        <p>Are you sure you want to delete this student?</p>
                        <div className="student-popup-buttons">
                            <button className="student-popup-btn-cancel" onClick={() => setShowDeletePopup(false)}>Cancel</button>
                            <button className="student-popup-btn-ok" onClick={confirmDelete}>Delete</button>
                        </div>
                    </div>
                </div>
            )}

            <div className="af-card">
                {/* Header */}
                <div className="af-header">
                    <div className="af-header-icon indigo">🎓</div>
                    <div className="af-header-text" style={{ flex: 1 }}>
                        <h2 className="af-title">Update Student</h2>
                        <p className="af-subtitle">Edit student details and enrolment information</p>
                    </div>
                    <button type="button" className="uf-delete-trigger" onClick={() => setShowDeletePopup(true)}>
                        Delete
                    </button>
                </div>

                {successMessage && <div className="af-alert af-alert-success" style={{ margin: "16px 32px 0" }}>{successMessage}</div>}
                {errorMessage   && <div className="af-alert af-alert-error"   style={{ margin: "16px 32px 0" }}>{errorMessage}</div>}

                <form onSubmit={handleSubmit}>
                    <div className="af-body">

                        {/* Personal Information */}
                        <div className="af-section">
                            <p className="af-section-title">Personal Information</p>
                            <Field label="Full Name" required error={errors.studentName}>
                                {inp("studentName", "e.g. Rahul Sharma")}
                            </Field>
                            <div className="af-row">
                                <Field label="Email Address(es)" required error={errors.emailId}>
                                    <div className={`af-email-tags-wrapper${errors.emailId ? " error-input" : ""}`}>
                                        {formData.emailId.map((email, i) => (
                                            <span key={i} className="af-email-tag">
                                                {email}
                                                <button type="button" className="af-email-tag-remove" onClick={() => removeEmail(i)}>×</button>
                                            </span>
                                        ))}
                                        <input
                                            type="email"
                                            className="af-email-tag-input"
                                            placeholder="Add email and press Enter"
                                            value={emailInput}
                                            onChange={e => setEmailInput(e.target.value)}
                                            onKeyDown={handleEmailKeyDown}
                                        />
                                    </div>
                                    <button type="button" className="af-email-add-btn" onClick={addEmail}>+ Add</button>
                                </Field>
                                <Field label="Status" error={errors.status}>
                                    {sel("status", STATUSES)}
                                </Field>
                        <Field label="Visibility" error={errors.isActive}>
                          <label className="af-switch">
                            <input
                              type="checkbox"
                              checked={formData.isActive !== false}
                              onChange={(e) => setFormData(p => ({ ...p, isActive: e.target.checked }))}
                            />
                            <span>{formData.isActive !== false ? "Active" : "Inactive"}</span>
                          </label>
                          <span className="af-hint">
                            Inactive students are hidden from lists and filter dropdowns across
                            the Admin and Trainer apps. Nothing is deleted, and the student keeps access to their own app.
                          </span>
                        </Field>
                        <Field label="Activity tracking">
                          <label className="af-switch">
                            <input
                              type="checkbox"
                              checked={formData.trackingEnabled !== false}
                              onChange={(e) => setFormData(p => ({ ...p, trackingEnabled: e.target.checked }))}
                            />
                            <span>{formData.trackingEnabled !== false ? "Tracking on" : "Tracking off"}</span>
                          </label>
                          <span className="af-hint">
                            On: the student's activity in the app (page visits, time spent, actions) is recorded.
                            Off: nothing they do is logged. The "Track logs" permission in User management must also allow it.
                          </span>
                        </Field>
                            </div>
                            <div className="af-row">
                                <Field label="Contact Number" required error={errors.contactNumber}>
                                    {inp("contactNumber", "10-digit mobile")}
                                </Field>
                                <Field label="Alternate Contact" required error={errors.alternateContactNumber}>
                                    {inp("alternateContactNumber", "Alternate number")}
                                </Field>
                            </div>
                            <div className="af-row">
                                <Field label="Date of Birth" error={errors.DOB}>
                                    {inp("DOB", "", "date")}
                                </Field>
                                <Field label="Heard From" required error={errors.heardFrom}>
                                    {sel("heardFrom", HEARD_FROM)}
                                </Field>
                            </div>
                        </div>

                        {/* Permanent Address */}
                        <div className="af-section">
                            <p className="af-section-title">Permanent Address</p>
                            <Field label="Address" required error={errors.permanentAddress}>
                                {textarea("permanentAddress", "House/Flat, Street, Area")}
                            </Field>
                            <div className="af-row">
                                <Field label="City" required error={errors.permanentAddressCity}>
                                    {inp("permanentAddressCity", "City")}
                                </Field>
                                <Field label="State" required error={errors.permanentAddressState}>
                                    {inp("permanentAddressState", "State")}
                                </Field>
                            </div>
                        </div>

                        {/* Alternate Address */}
                        <div className="af-section">
                            <div className="af-section-header-row">
                                <p className="af-section-title">Alternate Address</p>
                                <label className="af-same-address-label">
                                    <input
                                        type="checkbox"
                                        className="af-same-address-checkbox"
                                        checked={sameAsPermanent}
                                        onChange={(e) => {
                                            setSameAsPermanent(e.target.checked);
                                            if (e.target.checked) {
                                                setErrors(p => ({ ...p, alternateAddress: "", alternateAddressCity: "", alternateAddressState: "" }));
                                            }
                                        }}
                                    />
                                    Permanent address is the current address
                                </label>
                            </div>
                            {!sameAsPermanent && (
                                <>
                                    <Field label="Address" required error={errors.alternateAddress}>
                                        {textarea("alternateAddress", "House/Flat, Street, Area")}
                                    </Field>
                                    <div className="af-row">
                                        <Field label="City" required error={errors.alternateAddressCity}>
                                            {inp("alternateAddressCity", "City")}
                                        </Field>
                                        <Field label="State" required error={errors.alternateAddressState}>
                                            {inp("alternateAddressState", "State")}
                                        </Field>
                                    </div>
                                </>
                            )}
                        </div>

                        {/* Family Details */}
                        <div className="af-section">
                            <p className="af-section-title">Family Details</p>
                            <div className="af-row">
                                <Field label="Father's Name" required error={errors.fatherName}>
                                    {inp("fatherName", "Full name")}
                                </Field>
                                <Field label="Father's Occupation" required error={errors.fatherOccupation}>
                                    {inp("fatherOccupation", "Occupation")}
                                </Field>
                            </div>
                            <Field label="Father's Contact" required error={errors.fatherContactNumber}>
                                {inp("fatherContactNumber", "Contact number")}
                            </Field>
                            <div className="af-row">
                                <Field label="Mother's Name" required error={errors.motherName}>
                                    {inp("motherName", "Full name")}
                                </Field>
                                <Field label="Mother's Occupation" required error={errors.motherOccupation}>
                                    {inp("motherOccupation", "Occupation")}
                                </Field>
                            </div>
                            <Field label="Mother's Contact" required error={errors.motherContactNumber}>
                                {inp("motherContactNumber", "Contact number")}
                            </Field>
                        </div>

                        {/* Education */}
                        <div className="af-section">
                            <p className="af-section-title">Education</p>
                            <Field label="Last / Current Education" required error={errors.educationLevel}>
                                {sel("educationLevel", EDU_LEVELS)}
                            </Field>

                            {formData.educationLevel && (
                                <>
                                    <div className="af-row">
                                        <Field
                                            label={formData.educationLevel === "Schooling" ? "School Name" : "College / Institution"}
                                            required
                                            error={errors.schoolCollege}
                                        >
                                            <input
                                                type="text" name="schoolCollege"
                                                placeholder={formData.educationLevel === "Schooling" ? "School name" : "College / Institution name"}
                                                value={formData.schoolCollege}
                                                onChange={handleChange}
                                                className={errors.schoolCollege ? "error-input" : ""}
                                            />
                                        </Field>
                                        <Field label="City" error={errors.schoolCollegePlace}>
                                            <input
                                                type="text" name="schoolCollegePlace"
                                                placeholder="City"
                                                value={formData.schoolCollegePlace}
                                                onChange={handleChange}
                                            />
                                        </Field>
                                    </div>

                                    <div className="af-row">
                                        <Field
                                            label={formData.educationLevel === "Schooling" ? "Class / Grade" : "Branch / Specialization"}
                                            error={errors.branch}
                                        >
                                            <input
                                                type="text" name="branch"
                                                placeholder={formData.educationLevel === "Schooling" ? "e.g. 10th Grade" : "e.g. Computer Science"}
                                                value={formData.branch}
                                                onChange={handleChange}
                                            />
                                        </Field>
                                        <Field label="Score / CGPA / Marks" error={errors.score}>
                                            <input
                                                type="text" name="score"
                                                placeholder="e.g. 8.5 or 75%"
                                                value={formData.score}
                                                onChange={handleChange}
                                            />
                                        </Field>
                                    </div>

                                    <label className="af-checkbox-label">
                                        <input
                                            type="checkbox"
                                            checked={formData.currentlyPursuing}
                                            onChange={e => {
                                                setFormData(p => ({
                                                    ...p,
                                                    currentlyPursuing: e.target.checked,
                                                    completedIn: e.target.checked ? "" : p.completedIn,
                                                    completionYear: !e.target.checked ? "" : p.completionYear,
                                                }));
                                            }}
                                        />
                                        Currently Pursuing
                                    </label>

                                    {formData.currentlyPursuing ? (
                                        <Field label="Expected Completion Year" error={errors.completionYear}>
                                            <select name="completionYear" value={formData.completionYear} onChange={handleChange}>
                                                <option value="">Select year…</option>
                                                {FUTURE_YEARS.map(y => <option key={y} value={y}>{y}</option>)}
                                            </select>
                                        </Field>
                                    ) : (
                                        <Field label="Completed In" error={errors.completedIn}>
                                            <select name="completedIn" value={formData.completedIn} onChange={handleChange}>
                                                <option value="">Select year…</option>
                                                {EDU_YEARS.map(y => <option key={y} value={y}>{y}</option>)}
                                            </select>
                                        </Field>
                                    )}
                                </>
                            )}
                        </div>

                        {/* Course & Skills */}
                        <div className="af-section">
                            <p className="af-section-title">Course & Skills</p>
                            <Field label="Enrolling For Course" required error={errors.enrollingForCourse}>
                                <select name="enrollingForCourse" value={formData.enrollingForCourse}
                                    onChange={handleChange} className={errors.enrollingForCourse ? "error-input" : ""}>
                                    <option value="">Select course…</option>
                                    {courses.map(c => <option key={c._id} value={c.courseTitle}>{c.courseTitle}</option>)}
                                </select>
                            </Field>
                            <Field label="Enrolled For Course" error={errors.enrolledForCourse}>
                                <select name="enrolledForCourse" value={formData.enrolledForCourse} onChange={handleChange}>
                                    <option value="">Select course…</option>
                                    {courses.map(c => <option key={c._id} value={c.courseTitle}>{c.courseTitle}</option>)}
                                </select>
                            </Field>
                            <Field label="Skills Known Already" error={errors.skillsKnownAlready}>
                                {inp("skillsKnownAlready", "e.g. HTML, CSS, JavaScript")}
                            </Field>
                        </div>

                        {/* Batch & Financials */}
                        <div className="af-section">
                            <p className="af-section-title">Batch & Financials</p>
                            <Field label="Assign Batches" required error={errors.batchIds}>
                                <MultiSelectDropdown
                                    options={batchOptions}
                                    value={selectedBatchOptions}
                                    onChange={handleBatchChange}
                                    placeholder="Select one or more batches…"
                                    showCheckboxes
                                />
                                <span className="af-hint">
                                    The first batch selected becomes the student's primary batch.
                                </span>
                            </Field>
                            <div className="af-row">
                                <Field label="Total Fee (₹)" error={errors.totalFee}>
                                    {inp("totalFee", "e.g. 25000", "number")}
                                </Field>
                                <Field label="Fee Paid (₹)" error={errors.feePaid}>
                                    {inp("feePaid", "e.g. 10000", "number")}
                                </Field>
                            </div>
                        </div>

                        {/* Performance */}
                        <div className="af-section">
                            <p className="af-section-title">Performance</p>
                            <div className="af-action-strip">
                                <button type="button" className="af-secondary-btn"
                                    onClick={() => setShowUpdatePerformancesDialog(true)}>
                                    + Add Performance Entry
                                </button>
                            </div>
                            {errors.performance && <span className="af-error">{errors.performance}</span>}
                            <UpdateStudentPerformance
                                studentId={studentId}
                                openUpdatePerformancesDialogExternally={showUpdatePerformancesDialog}
                                setOpenUpdatePerformacesDialogExternally={setShowUpdatePerformancesDialog}
                            />
                        </div>
                    </div>

                    <div className="af-submit-row">
                        <button type="submit" className="af-submit" disabled={loading}>
                            {loading ? "Saving…" : "Update Student"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default UpdateStudent;
